"use client";

import { useEffect, useRef } from "react";

export function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    let isVisible = false;
    let currentShape: "circle" | "triangle" | "rectangle" = "circle";
    let shapeCheckCounter = 0;

    // Simplified element detection - only check every 3rd mousemove for performance
    const getElementShape = (
      element: Element | null
    ): "circle" | "triangle" | "rectangle" => {
      if (!element) return "circle";

      let el: Element | null = element;

      // Check element and parents up to 2 levels (reduced from 3)
      for (let i = 0; i < 2 && el; i++) {
        const tagName = el.tagName;

        // Interactive elements (links/buttons) - check this first for priority
        if (
          tagName === "A" ||
          tagName === "BUTTON" ||
          el.getAttribute("role") === "button"
        ) {
          return "triangle";
        }

        el = el.parentElement;
      }

      // Reset and check text tags on the original element
      el = element;
      if (
        [
          "P",
          "H1",
          "H2",
          "H3",
          "H4",
          "H5",
          "H6",
          "SPAN",
          "LI",
          "LABEL",
        ].includes(el.tagName)
      ) {
        return "rectangle";
      }

      return "circle";
    };

    const updateCursorPosition = (e: MouseEvent) => {
      // Update position immediately - transform is GPU accelerated
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;

      // Throttle shape detection - only check every 3rd mousemove
      shapeCheckCounter++;
      if (shapeCheckCounter % 3 === 0) {
        const shape = getElementShape(e.target as Element);

        if (shape !== currentShape) {
          currentShape = shape;

          // Remove old shape class
          cursor.classList.remove(
            "cursor-shape-circle",
            "cursor-shape-square",
            "cursor-shape-rectangle"
          );

          // Add new shape class with transition
          if (shape === "triangle") {
            cursor.classList.add("cursor-shape-square");
          } else if (shape === "rectangle") {
            cursor.classList.add("cursor-shape-rectangle");
          } else {
            cursor.classList.add("cursor-shape-circle");
          }
        }
      }

      if (!isVisible) {
        isVisible = true;
        cursor.style.opacity = "0.85";
      }
    };

    const handleMouseEnter = () => {
      isVisible = true;
      cursor.style.opacity = "0.85";
    };

    const handleMouseLeave = () => {
      isVisible = false;
      cursor.style.opacity = "0";
    };

    window.addEventListener("mousemove", updateCursorPosition, {
      passive: true,
    });
    document.addEventListener("mouseenter", handleMouseEnter);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", updateCursorPosition);
      document.removeEventListener("mouseenter", handleMouseEnter);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={cursorRef}
      className="pointer-events-none fixed left-0 top-0 z-[9999] bg-teal-500 will-change-transform cursor-shape-circle"
      style={{
        opacity: 0,
        transform: "translate(-50%, -50%)",
      }}
    />
  );
}
